import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ClipboardList, Users, FileText, Shield, Database, FileClock, Bell, Pill, Languages, BookMarked, TestTube2, BarChart3, Building2, Share2, Megaphone, Package } from "lucide-react";
import { useAuth } from "../lib/auth";
import { pruneOldDrafts } from "../lib/draft";
import { LogoTile, useClinic } from "../lib/clinic";
import { SupportModeBanner } from "./SupportModeBanner";
import api from "../lib/api";

function countDrafts() {
  try {
    let n = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("draft-visit-")) n++;
    }
    return n;
  } catch {
    return 0;
  }
}

export default function Layout({ children, contentClassName = "" }) {
  const { user, logout } = useAuth();
  const clinic = useClinic();   // MT-11C.2: header re-renders when the active tenant profile changes
  const nav = useNavigate();
  const [draftCount, setDraftCount] = useState(countDrafts());
  const [dueToday, setDueToday] = useState(0);
  const [refillsPending, setRefillsPending] = useState(0);

  useEffect(() => {
    pruneOldDrafts();
    // Badge counts CLOUD drafts (cross-device) + any local-only stragglers.
    const refresh = async () => {
      try {
        const r = await api.get("/visit-drafts");
        const serverPids = new Set((r.data.drafts || []).map((d) => d.patient_id));
        let localOnly = 0;
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith("draft-visit-") && !serverPids.has(k.slice("draft-visit-".length))) localOnly++;
        }
        setDraftCount(serverPids.size + localOnly);
      } catch {
        setDraftCount(countDrafts());
      }
    };
    refresh();
    window.addEventListener("drafts-changed", refresh);
    const iv = setInterval(refresh, 30000);
    (async () => {
      try {
        const r = await api.get("/reminders/counts");
        setDueToday((r.data.today || 0) + (r.data.overdue || 0));
      } catch (e) { console.debug("[silent]", e); }
      try {
        const rr = await api.get("/refills");
        setRefillsPending((rr.data.refills || []).filter((x) => !x.refilled).length);
      } catch (e) { console.debug("[silent]", e); }
    })();
    return () => {
      window.removeEventListener("drafts-changed", refresh);
      clearInterval(iv);
    };
  }, []);
  const isClinicAdmin = user?.role === "doctor" || user?.role === "admin" || user?.role === "owner";   // MT-12B: owner = administrative authority
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <SupportModeBanner user={user} />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-4 py-2 min-h-14 flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-1 min-w-0 flex-wrap items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="brand-link">
              <LogoTile className="h-9 w-9" padding="p-0.5" />
              <div className="min-w-0 break-words max-w-xs">
                <div className="font-heading text-sm font-semibold leading-tight" data-testid="header-clinic-name">{clinic.name}</div>
                <div className="text-[10px] tracking-[0.1em] uppercase text-slate-500 leading-none mt-1">Clinical Suite</div>
              </div>
            </Link>
            <nav className="hidden md:flex flex-wrap items-center gap-1 text-sm min-w-0" data-testid="main-navigation">
              <Link to="/dashboard" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-dashboard"><Users size={14} /> Patients</Link>
              <Link to="/drafts" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 relative" data-testid="nav-drafts">
                <FileClock size={14} /> Drafts
                {draftCount > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-pink-600 text-white tabular"
                    data-testid="drafts-badge"
                  >
                    {draftCount}
                  </span>
                )}
              </Link>
              <Link to="/reminders" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 relative" data-testid="nav-reminders">
                <Bell size={14} /> Reminders
                {dueToday > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-red-600 text-white tabular"
                    data-testid="reminders-badge"
                  >
                    {dueToday}
                  </span>
                )}
              </Link>
              <Link to="/refills" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 relative" data-testid="nav-refills">
                <Pill size={14} /> Refills
                {refillsPending > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-amber-600 text-white tabular"
                    data-testid="refills-badge"
                  >
                    {refillsPending}
                  </span>
                )}
              </Link>
              <Link to="/templates" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-templates"><FileText size={14} /> Templates</Link>
              <Link to="/network" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-network"><Share2 size={14} /> Network</Link>
              {user?.is_superadmin && (
                <Link to="/superadmin" className="px-3 py-1.5 rounded hover:bg-amber-50 text-amber-800 flex items-center gap-2" data-testid="nav-superadmin"><Shield size={14} /> Platform</Link>
              )}
              {isClinicAdmin && (
                <Link to="/analytics" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-analytics"><BarChart3 size={14} /> Analytics</Link>
              )}
              <Link to="/translations" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-translations"><Languages size={14} /> Translations</Link>
              <Link to="/settings/guidelines" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-guidelines"><BookMarked size={14} /> Guidelines</Link>
              <Link to="/settings/lab-packages" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-lab-packages"><TestTube2 size={14} /> Lab prefs</Link>
              {isClinicAdmin && (
                <Link to="/settings/medicines" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-medicines"><Package size={14} /> Medicines</Link>
              )}
              {isClinicAdmin && (
                <Link to="/settings/clinic" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-clinic-profile"><Building2 size={14} /> Clinic profile</Link>
              )}
              {isClinicAdmin && (
                <Link to="/settings/staff" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-staff"><Users size={14} /> Staff</Link>
              )}
              <Link to="/admin/import" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-import"><Database size={14} /> Import</Link>
              <Link to="/audit" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-audit"><Shield size={14} /> Audit</Link>
              {isClinicAdmin && (
                <Link to="/pitch" className="px-3 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid="nav-pitch"><Megaphone size={14} /> Pitch</Link>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-3 max-w-full">
            {user && (
              <>
                <div className="text-right leading-tight min-w-0 break-words">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className={`text-[10px] uppercase tracking-wider font-semibold inline-block px-1.5 py-0.5 rounded border mt-0.5 ${
                    user.role === "doctor" ? "bg-pink-50 text-pink-700 border-pink-200"
                    : user.role === "reception" ? "bg-sky-50 text-sky-700 border-sky-200"
                    : user.role === "nurse" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : user.role === "admin" ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                  }`} data-testid="header-role-badge">
                    {user.role || "user"}
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="p-2 rounded hover:bg-slate-100"
                  data-testid="logout-btn"
                  aria-label="Log out"
                >
                  <LogOut size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={`mx-auto max-w-[1400px] px-6 py-6 ${contentClassName}`}>{children}</main>

      <footer className="mx-auto max-w-[1400px] px-6 py-6 text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 mt-8">
        This software is intended to assist qualified healthcare professionals with clinical documentation and
        prescription generation. It does not independently diagnose, prescribe, or replace professional clinical
        judgement. The prescribing doctor remains responsible for verifying medicine selection, dosage,
        interactions, contraindications and patient-specific safety.
      </footer>
    </div>
  );
}
